'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useDebounce } from '@packages/shared/src/hooks/useDebounce/useDebounce';
import { useSession } from '@packages/shared/src/hooks/useSession/useSession';
import { useRouterPush } from '@packages/shared/src/hooks/useRouterPush/useRouterPush';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinSearch } from '@packages/tracking/src/types/events';

import { Input } from '@packages/shared/src/ui/Input/Input';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useIsHydrated } from '@packages/shared/src/hooks/useIsHydrated/useIsHydrated';
import { useSearchQuery } from '../../useSearchQuery';
import type { SuggestionTrackingData } from './types';
import { SearchFieldPopup } from './SearchField.Popup';

const messages = defineMessages({
  placeholder: { id: 'searchbar.placeholder', defaultMessage: 'Lieblingsartikel suchen...' },
  ariaLabelInput: {
    id: 'searchbar.input.aria-label',
    defaultMessage: 'Suchen',
  },
  ariaLabelResetButton: {
    id: 'searchbar.resetbutton.aria-label',
    defaultMessage: 'Zurücksetzen',
  },
  ariaLabelButton: {
    id: 'searchbar.button.aria-label',
    defaultMessage: 'Suchen',
  },
  searchBarEmptyAlert: {
    id: 'searchbar.invalid.empty',
    defaultMessage: 'Bitte geben Sie zunächst Ihren Suchbegriff ein.',
  },
});

export const INITIAL_INPUT_VALUE = '';
/**
 * If `true`, glycerin `searchTermEntered` will be overridden for suggestions, and the suggestion term will be used.
 * Even if `true`, if the suggestion does not have a term (e.g. category/article suggestions), the input value will still be used.
 *
 * If `false` for `searchTermEntered` the input value will be used in all cases.
 * This conforms closer to the glycerin schema, but is currently less desired from a BI perspective, so this flag will be left in for now until a final decision is made
 *
 * Previous behaviour was to always use the suggestion term, even if it did not exist (then as an empty string), which led to issues with tracking
 */
export const USE_SUGGESTION_TERM_FOR_TRACKING_AS_SEARCH_TERM_ENTERED = false;

export interface SearchFieldProps {
  /**
   * Sets focus on mounting
   * @default false
   */
  autofocus?: boolean;
  /** If `true`, don't show suggestions or history, and don't reflect current route in input
   * @default false
   * */
  isolated?: boolean;
  /** A search was submitted from any source (user input, history, suggest, ...), and will lead to a route change */
  onNavigate?: () => void;
  /** Optionally modify the URL that will be navigated to before navigation happens, e.g. to append query params */
  postProcessSearchUrl?: (url: string) => string;
}

/** Integrated search textfield component with iconbutton used in he ader */
export const SearchField = ({
  autofocus,
  isolated,
  onNavigate,
  postProcessSearchUrl,
}: SearchFieldProps) => {
  const searchQuery = useSearchQuery();
  const { formatMessage } = useIntl();
  const routerPush = useRouterPush();
  const { language, localizeUrl } = useI18n();
  // INFO: needs to get the jwt token here and provide to the popover components to avoid suspense + swr anti-pattern that the key can be null. update when swr completly implements suspense concept in further versions
  const { jwtToken } = useSession();
  const { addNotification } = useNotification();

  const initialValue = searchQuery && !isolated ? searchQuery : INITIAL_INPUT_VALUE;
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value);
  const searchTermForSuggestions =
    debouncedValue === INITIAL_INPUT_VALUE ? undefined : debouncedValue;

  const [hasPopupRelevantFocus, setHasPopupRelevantFocus] = useState(false);
  const showPopup = jwtToken && !isolated && hasPopupRelevantFocus;

  const { isHydrated } = useIsHydrated();

  const inputRef = useRef<HTMLInputElement>(null);

  const dispatchGtmEvent = useTracking();

  const focusOnInput = () => {
    inputRef.current?.focus();
  };
  const focusOnWrapper = () => {
    setHasPopupRelevantFocus(false);
  };

  const shiftFocusFromInputToWrapper = useCallback(() => {
    if (document.activeElement === inputRef.current) {
      focusOnWrapper();
    }
  }, []);

  const updateInput = (newValue: string) => {
    setValue(newValue);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
    setHasPopupRelevantFocus(true);
  };

  const handleSubmitButtonFocus = () => {
    setHasPopupRelevantFocus(true);
  };

  const handleWrapperBlur = (event: React.FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setHasPopupRelevantFocus(false);
    }
  };

  /**
   * Dismisses mobile keyboard and triggers navigation callback.
   * @param onComplete - Optional callback to run after blur in the same deferred execution
   */
  const dismissKeyboardAndNavigate = useCallback(
    (onComplete?: () => void) => {
      onNavigate?.();
      // Defer blur to avoid interfering with React's state updates (e.g., loading indicator)
      // All deferred operations are consolidated in a single setTimeout to ensure deterministic ordering
      setTimeout(() => {
        inputRef.current?.blur();
        onComplete?.();
      });
    },
    [onNavigate],
  );

  // if input is empty show alert, else go to search route
  const handleSubmit = () => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      addNotification({
        severity: 'error',
        message: formatMessage(messages.searchBarEmptyAlert),
        duration: 2000,
      });
      return;
    }

    // should work same as in the "old" shop system
    const url = `/s/${encodeURIComponent(trimmedValue.replace(/[\s]+/g, '+')).replaceAll('%2B', '+')}/`;
    const finalUrl = postProcessSearchUrl?.(url) ?? url;
    const localizedUrl = localizeUrl(finalUrl, language);

    routerPush(localizedUrl);

    dismissKeyboardAndNavigate(shiftFocusFromInputToWrapper);
    dispatchGtmEvent<GTMEventGlycerinSearch>({
      event: 'Search',
      SearchData: { searchTermEntered: trimmedValue },
    });
  };

  const handleAcceptSuggestion = (suggestionTrackingData: SuggestionTrackingData) => {
    const searchTermEntered = USE_SUGGESTION_TERM_FOR_TRACKING_AS_SEARCH_TERM_ENTERED
      ? (suggestionTrackingData.suggestionUsed.term ?? value.trim())
      : value.trim();

    // basically just optimistic update, after navigation the input will be updated anyways
    if (suggestionTrackingData.suggestionUsed.term) {
      updateInput(suggestionTrackingData.suggestionUsed.term);
    }

    dispatchGtmEvent<GTMEventGlycerinSearch>({
      event: 'Search',
      SearchData: {
        searchTermEntered,
        ...suggestionTrackingData,
      },
    });

    // Deferred operations (blur + focusOnWrapper) are consolidated in a single setTimeout
    // The defer is needed because if triggered by keyboard navigation, the closing popup would prevent the anchor from navigating
    dismissKeyboardAndNavigate(focusOnWrapper);
  };

  const handleAdoptSuggestion = (suggestion: string) => {
    updateInput(suggestion);
    focusOnInput();
  };

  const handleClearButton = () => {
    setValue(INITIAL_INPUT_VALUE);
    focusOnInput();
  };

  //  A change in `initialValue` means a change in the route
  //  In this case we want to reset the input and blur, similar to how everything is on initial render
  useEffect(() => {
    setValue(initialValue);
    shiftFocusFromInputToWrapper();
  }, [initialValue, shiftFocusFromInputToWrapper]);

  //  A change in `autofocus` should focus the input if need be
  //  Caution: This effect needs to run after the previous effect, which blurs the input
  //  Otherwise the call of `blur` overrides the call of `focus`
  useEffect(() => {
    if (autofocus && isHydrated) {
      focusOnInput();
    }
  }, [autofocus, isHydrated]);

  return (
    <form
      role="search"
      className="relative flex items-center"
      onBlur={handleWrapperBlur}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Input
        autoComplete="off"
        placeholder={formatMessage(messages.placeholder)}
        type="search"
        //  workaround for e2e testing, to be able to target the "main" search field over other search fields on the page
        //  TODO make a11y improvements to make the search fields distinguishable without this workaround
        aria-label={formatMessage(messages.ariaLabelInput)}
        aria-placeholder={formatMessage(messages.placeholder)}
        data-isolated
        disabled={!isHydrated}
        enterKeyHint="search"
        value={value}
        onChange={(event) => {
          updateInput(event.target.value);
        }}
        className={`rounded-[inherit] rounded-tr-none rounded-br-none pr-[4rem] text-ellipsis ${hasPopupRelevantFocus ? 'border-color-primary' : 'border-color-grey-dark'}`}
        inputContainerClassName="search-field"
        containerClassName="w-full relative"
        onFocus={handleInputFocus}
        ref={inputRef}
      >
        {value && (
          <Button
            onClick={handleClearButton}
            variant="icon-flat"
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              handleClearButton();
            }}
            aria-label={formatMessage(messages.ariaLabelResetButton)}
            className="absolute top-1/2 right-0 mx-1 -translate-y-1/2"
          >
            <span className="icon-emp-cancel" />
          </Button>
        )}
      </Input>
      <Button
        variant="icon-only"
        type="submit"
        color={hasPopupRelevantFocus ? 'primary' : 'default'}
        className={`focus-within:standard-focus-outline ml-[-1px] h-[44px] w-[44px] flex-shrink-0 rounded-tl-none rounded-bl-none text-[32px] ${
          hasPopupRelevantFocus
            ? 'border-color-primary bg-color-primary border'
            : 'border-color-grey-dark'
        }`}
        disabled={!isHydrated}
        aria-label={formatMessage(messages.ariaLabelButton)}
        onFocus={handleSubmitButtonFocus}
      >
        <span
          className={`icon-emp-search ${hasPopupRelevantFocus && 'text-color-primary-contrasttext'}`}
        />
      </Button>
      {showPopup && (
        <SearchFieldPopup
          searchTerm={searchTermForSuggestions}
          jwtToken={jwtToken}
          onAccept={handleAcceptSuggestion}
          onAdopt={handleAdoptSuggestion}
        />
      )}
    </form>
  );
};

//  required for next/dynamic
//  eslint-disable-next-line import-x/no-default-export
export default SearchField;
